import { AppStatuses, ChannelTypes, Dealer, LeaseOptions } from '../../gql/graphql';
import { inIframe } from '../window/window.helpers';
import { EntrypointValues } from '../../constants/query-params';

/**
 * greatestApprovalAmountHelper gets the greatest approval amount from all lease options
 */
const greatestApprovalAmountHelper = (leaseOptions: LeaseOptions[] | null | undefined, cartTotal: number) => {
  if (!leaseOptions || leaseOptions.length === 0) {
    return null;
  }

  if (leaseOptions.length === 1) {
    return leaseOptions[0].approvedAmount;
  }

  var leaseOptionsWithinMinimumSpend = leaseOptions.filter((leaseOption) => cartTotal === 0 || Number(leaseOption.minimumSpend) <= cartTotal);

  if (leaseOptionsWithinMinimumSpend.length === 0) {
    return null;
  }

  return Math.max(...leaseOptionsWithinMinimumSpend.map((leaseOption) => Number(leaseOption.approvedAmount))).toString();
};

/**
 * selectLeaseHelper gets the lease with the greatest approval amount
 */
const selectLeaseHelper = (leaseOptions: LeaseOptions[] | null | undefined): LeaseOptions | null => {
  if (!leaseOptions || leaseOptions.length === 0) return null;

  return leaseOptions.reduce((prevVal, curVal) => (Number(curVal?.approvedAmount) > Number(prevVal?.approvedAmount) ? curVal : prevVal));
};

/**
 * selectApprovalLeaseOptions picks which set of lease options an approval screen should display.
 *
 * Once the application has been decisioned its own options are authoritative: they are what the
 * merchant portal shows, what the contract is cut from, and what LAS enforces as the cart cap.
 * The OTB options from customer eligibility describe the customer's pre-existing open-to-buy line,
 * are fetched before apply and never refreshed, and can name terms the application does not offer.
 * They are only a fallback for before the application carries an approved amount. (OR-2804)
 */
const selectApprovalLeaseOptions = (
  applicationOptions: LeaseOptions[] | null | undefined,
  otbOptions: LeaseOptions[] | null | undefined,
): LeaseOptions[] | null | undefined => {
  const decisionedOption = selectLeaseHelper(applicationOptions);
  if (decisionedOption && Number(decisionedOption.approvedAmount) > 0) {
    return applicationOptions;
  }

  return otbOptions?.length ? otbOptions : applicationOptions;
};

/**
 * selectLeaseHelper gets the lease with the lowest minimum spend
 */
const getLowestMinimumSpend = (leaseOptions: LeaseOptions[] | null | undefined) => {
  if (!leaseOptions || leaseOptions.length === 0) return null;

  return leaseOptions.length === 1 ? Number(leaseOptions[0].minimumSpend) : Math.min(...leaseOptions.map((leaseOption) => Number(leaseOption.minimumSpend)));
};

const getEcommApprovalOnly = (dealer?: Dealer) => {
  // Currently only allowing to continue through approval if in full screen
  // Not brick and mortar AND not virtual card
  let isEcomm;
  let isVC;
  if (!!dealer) {
    isEcomm = dealer?.channelType !== ChannelTypes.BrickAndMortar;
    isVC = dealer?.isVirtualCard;
  }
  const isModal = inIframe();
  return !isModal && !!isEcomm && !isVC;
};

const canSkipItemEntry = (dealer: Dealer, contractSummariesExists: boolean, entrypoint: string) =>
  entrypoint === EntrypointValues.MobileEmbedded && contractSummariesExists && dealer.isVirtualCard;

export { greatestApprovalAmountHelper, selectLeaseHelper, selectApprovalLeaseOptions, getLowestMinimumSpend, getEcommApprovalOnly, canSkipItemEntry };
